import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type MutableRefObject, type ReactNode,
} from "react";
import type { components } from "@cntrl/api-client";
import { useReplaceLineup } from "../../app/hooks/useShows.js";
import { useAccount } from "../../app/hooks/useAccount.js";
import { useSectionSave } from "./EditorSaveRegistry.js";

type LineupSlotDto = components["schemas"]["LineupSlotDto"];

/**
 * The editor's ONE live lineup draft, shared by section 02 (Lineup, the writer) and section 05 (Run of Show,
 * a reader) — ART-209.
 *
 * Before this the two sections each read the lineup separately: Lineup held an unsaved local draft, Run of
 * Show read the SERVER lineup (`detail.lineup`). So an act you added but had not yet saved was invisible in
 * Run of Show — "Added both bands but they dont show in Run of show to edit their time … once you save they
 * show" (Ryan). Lifting the draft here lets a freshly added act appear in the grid immediately, and lets its
 * set time save in the SAME footer Save: the fan-out commits Lineup (order 2) before Run of Show (order 5),
 * so by the time Run of Show writes its rows the new act has a real `slotId` — which Run of Show reads back
 * out of {@link LineupDraft.slotsRef}.
 *
 * <b>Why a stable `clientId`.</b> A run-of-show row is attributed by `slotId`, but a not-yet-saved act has
 * `slotId: null` — so it cannot key Run of Show's per-act state. Every draft slot therefore carries a
 * `clientId` that is stable across the whole editing session, and Run of Show keys on THAT. The `slotId`
 * arrives on commit and is looked up by `clientId` at save time.
 */
export type DraftSlot = LineupSlotDto & { clientId: string };

let seq = 0;
/** Monotonic, module-local — stable within a session and predictable in a test. Never persisted. */
function newClientId(): string {
  seq += 1;
  return `ls-${seq}`;
}

function withClientId(slot: LineupSlotDto): DraftSlot {
  return { ...slot, clientId: newClientId() };
}

/** Your own band's chip — seeded locally so the bill is never empty, even on a show that does not exist yet.
 *  Nameless until the account read lands (the name-stamp effect fills it; the server stamps it on save). */
function ownChip(ownerBandId: string | undefined, name: string): DraftSlot {
  return {
    clientId: newClientId(),
    slotId: null,
    position: 0,
    slotRole: "headliner",
    artistName: name,
    spotifyArtistId: null,
    spotifyImageUrl: null,
    friendBandId: ownerBandId ?? null,
  };
}

export interface LineupDraft {
  /** The live bill, in the order it was built (Run of Show does its own chronological sort). */
  slots: DraftSlot[];
  /** A ref mirror of `slots`, always current — read by Run of Show's save, which runs in the same fan-out
   *  tick as Lineup's commit and so cannot wait for a re-render to see the freshly assigned `slotId`s. */
  slotsRef: MutableRefObject<DraftSlot[]>;
  /** The band whose show this is; its slot is OUR act and is unremovable. */
  ownerBandId: string | undefined;
  /** Anything unsaved to write? Drives the footer's dirty state and the section's own Save. */
  dirty: boolean;
  /** True while a lineup commit is in flight. */
  saving: boolean;
  /** Append an act (a pick, or committed free text). A registered band already on the bill is never doubled. */
  addAct: (name: string, spotifyArtistId: string | null, imageUrl: string | null, friendBandId?: string | null, avatarMediaId?: string | null) => void;
  /** Patch a slot by its stable client id. */
  updateSlot: (clientId: string, patch: Partial<LineupSlotDto>) => void;
  /** Remove a slot by its stable client id. */
  removeSlot: (clientId: string) => void;
  /** Commit the lineup, preserving each slot's `clientId` so run-of-show times keyed on it survive the save. */
  commit: (showId: string) => Promise<void>;
}

const Ctx = createContext<LineupDraft | null>(null);

/** A stable empty reference, so a create screen (no `detail`) does not hand the resync a fresh array each
 *  render — which would make its dependency change every time and loop. */
const EMPTY_LINEUP: LineupSlotDto[] = [];

export function LineupDraftProvider({ serverLineup, ownerBandId, children }: {
  /** The server's lineup for this show (`detail.lineup`); null/undefined until the show is loaded/created. */
  serverLineup: LineupSlotDto[] | null | undefined;
  ownerBandId: string | undefined;
  children: ReactNode;
}) {
  const server = serverLineup ?? EMPTY_LINEUP;
  const replaceLineup = useReplaceLineup();
  const account = useAccount();
  const ownBandName = account.data?.artist ?? "";

  const slotsRef = useRef<DraftSlot[]>([]);
  const [slots, setSlotsState] = useState<DraftSlot[]>(() => {
    const seeded = server.map(withClientId);
    const withOwn = seeded.some((s) => s.friendBandId === ownerBandId)
      ? seeded
      : [...seeded, ownChip(ownerBandId, ownBandName)];
    slotsRef.current = withOwn;
    return withOwn;
  });
  const [dirty, setDirty] = useState(false);

  /**
   * The one setter — updates the ref SYNCHRONOUSLY (not inside the state updater, which React defers to render
   * time). This is what lets Run of Show's save read the freshly committed slotIds during the footer fan-out:
   * Lineup commits (order 2) and Run of Show (order 5) run in the same synchronous tick, before any re-render,
   * so a ref written only at render time would still be stale when Run of Show reads it. Functional updaters
   * resolve against the ref (always current), so two calls in one tick compose correctly.
   */
  const setSlots = useCallback((next: DraftSlot[] | ((prev: DraftSlot[]) => DraftSlot[])) => {
    const resolved = typeof next === "function" ? (next as (p: DraftSlot[]) => DraftSlot[])(slotsRef.current) : next;
    slotsRef.current = resolved;
    setSlotsState(resolved);
  }, []);

  // The account read usually lands after the first paint, so a chip seeded nameless gets its name here.
  useEffect(() => {
    if (!ownBandName) return;
    setSlots((prev) => (prev.some((sl) => sl.friendBandId === ownerBandId && !sl.artistName)
      ? prev.map((sl) => (sl.friendBandId === ownerBandId && !sl.artistName ? { ...sl, artistName: ownBandName } : sl))
      : prev));
  }, [ownBandName, ownerBandId, setSlots]);

  // Adopt the server's lineup when it changes and there are no unsaved edits. Comparing inside the updater and
  // returning `prev` unchanged is what keeps this from looping; a dirty draft outranks a refetch. The own chip
  // is REUSED across a resync (its clientId kept), so a run-of-show time keyed on it is not orphaned.
  useEffect(() => {
    if (dirty) return;
    setSlots((prev) => {
      const same = prev.length === server.length && prev.every((p, i) =>
        p.slotId === server[i].slotId
        && p.artistName === server[i].artistName
        && p.slotRole === server[i].slotRole
        && p.friendBandId === server[i].friendBandId);
      if (same) return prev;
      const existingOwn = prev.find((sl) => sl.friendBandId === ownerBandId);
      const next = server.map(withClientId);
      if (next.some((sl) => sl.friendBandId === ownerBandId)) return next;
      return [...next, existingOwn ?? ownChip(ownerBandId, ownBandName)];
    });
  }, [server, dirty, ownerBandId, ownBandName, setSlots]);

  const addAct = useCallback<LineupDraft["addAct"]>((name, spotifyArtistId, imageUrl, friendBandId = null, avatarMediaId = null) => {
    const clean = name.trim();
    if (clean === "") return;
    setSlots((prev) =>
      // A registered band plays ONE slot — never doubled. Free text (no id) may repeat.
      friendBandId && prev.some((s) => s.friendBandId === friendBandId)
        ? prev
        : [...prev, {
            clientId: newClientId(), slotId: null, position: 0, slotRole: "support",
            artistName: clean, spotifyArtistId, spotifyImageUrl: imageUrl, friendBandId,
            // Carried from the pick so an unsaved act shows its photo before the server resolves it on save.
            friendBandAvatarMediaId: avatarMediaId,
          }]);
    setDirty(true);
  }, [setSlots]);

  const updateSlot = useCallback<LineupDraft["updateSlot"]>((clientId, patch) => {
    setSlots((prev) => prev.map((s) => (s.clientId === clientId ? { ...s, ...patch } : s)));
    setDirty(true);
  }, [setSlots]);

  const removeSlot = useCallback<LineupDraft["removeSlot"]>((clientId) => {
    setSlots((prev) => prev.filter((s) => s.clientId !== clientId));
    setDirty(true);
  }, [setSlots]);

  const commit = useCallback<LineupDraft["commit"]>(async (showId) => {
    // Our own chip rides the save even nameless — the server stamps it. Everyone else needs a name.
    const toSend = slotsRef.current
      .filter((s) => (s.artistName ?? "").trim() !== "" || s.friendBandId === ownerBandId)
      .map((s, i) => ({ ...s, position: i, artistName: (s.artistName ?? "").trim() }));
    const rows = await replaceLineup.mutateAsync({
      showId,
      slots: toSend.map(({ clientId: _drop, ...slot }) => slot),
    });
    // Re-map the server rows back onto the draft, preserving each slot's clientId by position (the PUT returns
    // rows in the order sent). This is what carries a slotId back to a run-of-show time keyed on the clientId.
    setSlots(rows.map((r, i) => ({ ...r, clientId: toSend[i]?.clientId ?? newClientId() })));
    setDirty(false);
  }, [ownerBandId, replaceLineup, setSlots]);

  // The lineup's entry in the footer's fan-out. Registered here (not in the section) so the ONE commit path is
  // shared by the footer Save and the section's standalone button, and so Run of Show's save — which reads
  // `slotsRef` — always runs after this has landed the slotIds (order 2 before order 5).
  useSectionSave("lineup", {
    label: "Lineup",
    order: 2,
    isDirty: () => dirty,
    save: async (id) => { await commit(id); },
  });

  const value = useMemo<LineupDraft>(
    () => ({ slots, slotsRef, ownerBandId, dirty, saving: replaceLineup.isPending, addAct, updateSlot, removeSlot, commit }),
    [slots, ownerBandId, dirty, replaceLineup.isPending, addAct, updateSlot, removeSlot, commit],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLineupDraft(): LineupDraft {
  const v = useContext(Ctx);
  if (!v) throw new Error("useLineupDraft must be used within a LineupDraftProvider");
  return v;
}
